import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Team } from './types';
import { SidebarProvider } from './SidebarProvider';
import { useSidebar } from './useSidebar';
import SidebarNavigation from './SidebarNavigation';
import TeamsSection from './TeamsSection';
import UserProfile from './UserProfile';
import NewTeamDialog from './NewTeamDialog';

const SidebarContent: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isNewTeamDialogOpen, setIsNewTeamDialogOpen] = useState(false);
  const { expanded, setExpanded, toggleSidebar } = useSidebar();
  const navigate = useNavigate();

  const handleCreateTeam = (newTeam: Team) => {
    setTeams([...teams, newTeam]);
  };

  // Check if we're on mobile
  const isMobile = window.innerWidth < 768;

  return (
    <>
      <aside
        className={cn(
          "h-screen fixed top-0 left-0 z-navbar flex flex-col bg-sidebar shadow-md transition-all duration-300 border-r",
          expanded ? "w-sidebar-expanded" : "w-sidebar-collapsed"
        )}
        onMouseEnter={() => !isMobile && setExpanded(true)}
        onMouseLeave={() => !isMobile && setExpanded(false)}
      >
        <div className="flex items-center justify-between px-4 h-16 border-b">
          {expanded ? (
            <div className="flex items-center">
              <span className="font-bold text-lg text-accent">EvalOps</span>
            </div>
          ) : (
            <div className="mx-auto">
              <span className="font-bold text-xl text-accent">E</span>
            </div>
          )}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={toggleSidebar}
            className="hover:bg-sidebar-accent p-2" 
            aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            <Menu className="h-6 w-6" /> 
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          <SidebarNavigation />
          
          {expanded && <TeamsSection 
            teams={teams} 
            setTeams={setTeams}
            setIsNewTeamDialogOpen={setIsNewTeamDialogOpen}
          />}
        </nav>

        {expanded && <UserProfile />}
      </aside>

      <NewTeamDialog 
        open={isNewTeamDialogOpen} 
        onOpenChange={setIsNewTeamDialogOpen}
        onCreateTeam={handleCreateTeam}
      />
    </>
  );
};

const Sidebar: React.FC = () => {
  return (
    <SidebarProvider>
      <SidebarContent />
    </SidebarProvider>
  );
};

export default Sidebar;
